import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { AutoSizer, Grid } from 'react-virtualized';
import { useMediaQuery } from 'react-responsive';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';

import SlideThumbnailSortable, { SlideThumbnail } from '../SlideThumbnail';
import { isIntroLayout } from '../../../../../../helpers';
import { breakpointDown } from '../../../../../../../../../../../../services/responsive/responsive';

const propTypes = {
  slideshowSlides: PropTypes.arrayOf(
    PropTypes.shape({
      photo: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  ).isRequired,
  currentSlideId: PropTypes.string,
  onThumbnailClick: PropTypes.func.isRequired,
  onMoveSlide: PropTypes.func.isRequired,
  onDeletePhoto: PropTypes.func.isRequired,
  onDeleteLayout: PropTypes.func.isRequired,
  loadingMore: PropTypes.bool,
  onLoadMore: PropTypes.func.isRequired,
  hasIntroSlide: PropTypes.bool,
  onReorderSlides: PropTypes.func.isRequired,
  className: PropTypes.string,
};
const SlidesSection = ({
  slideshowSlides,
  slidesTotal,
  slidesOffset,
  currentSlideId,
  onThumbnailClick,
  onMoveSlide,
  onDeletePhoto,
  onDeleteLayout,
  loadingMore,
  onLoadMore,
  hasIntroSlide,
  onReorderSlides,
  className,
}) => {
  const isMobile = useMediaQuery(breakpointDown('sm'));
  const [draggingSlide, setDraggingSlide] = useState(null);

  // Drag and Drop Context Related

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      delay: 150,
      tolerance: 5,
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250,
      tolerance: 5,
    },
  });

  const onDragEnd = ({ active, over }) => {
    if (!active || !over) {
      return;
    }

    if (active.id !== over.id) {
      const oldIndex = slideshowSlides.findIndex(
        ({ photo, asset_image, account_card }) =>
          (photo && photo.id === active.id) ||
          (asset_image && asset_image.id === active.id) ||
          (account_card && account_card.id === active.id),
      );
      const newIndex = slideshowSlides.findIndex(
        ({ photo, asset_image, account_card }) =>
          (photo && photo.id === over.id) ||
          (asset_image && asset_image.id === over.id) ||
          (account_card && account_card.id === over.id),
      );
      const reorderedSlides = arrayMove(slideshowSlides, oldIndex, newIndex);
      onReorderSlides(reorderedSlides);
    }
  };

  const onDragStart = ({ active }) => {
    setDraggingSlide(active.data.current.slide);
  };

  // --

  const onSectionRendered = ({
    columnOverscanStopIndex,
    rowOverscanStopIndex,
  }) => {
    if (!onLoadMore || loadingMore) {
      return;
    }

    if (slideshowSlides.length === slidesTotal) {
      return;
    }

    if (
      isMobile &&
      (slidesOffset >= slideshowSlides.length ||
        columnOverscanStopIndex + 1 + slidesOffset >= slideshowSlides.length)
    ) {
      onLoadMore();
    }

    if (
      slidesOffset >= slideshowSlides.length ||
      rowOverscanStopIndex + 1 + slidesOffset >= slideshowSlides.length
    ) {
      onLoadMore();
    }
  };

  const cellRenderer = ({ columnIndex, rowIndex, key, style }) => {
    const slideIndex = isMobile ? columnIndex : rowIndex;
    const slideshowSlide = slideshowSlides[slideIndex];
    const isIntroSlide = isIntroLayout(slideshowSlide);
    const isActive =
      currentSlideId &&
      (currentSlideId === slideshowSlide.photo?.id ||
        currentSlideId === slideshowSlide.asset_image?.id ||
        currentSlideId === slideshowSlide.account_card?.id);

    return (
      <SlideThumbnailSortable
        key={key}
        style={style}
        slideshowSlide={slideshowSlide}
        isActive={isActive}
        onClick={() =>
          onThumbnailClick({
            slideId:
              slideshowSlide.photo?.id ??
              slideshowSlide.asset_image?.id ??
              slideshowSlide.account_card?.id,
            slideIdx: isActive ? undefined : slideIndex,
          })
        }
        onDelete={() =>
          slideshowSlide.account_card
            ? onDeleteLayout({
                account_card_id: slideshowSlide.account_card.id,
                slideIdx: slideIndex,
              })
            : onDeletePhoto({
                photoId:
                  slideshowSlide.asset_image?.id ?? slideshowSlide.photo?.id,
                photoIdx: slideIndex,
                isIntroSlide: !!slideshowSlide.asset_image,
              })
        }
        onMoveUp={
          !onMoveSlide || isIntroSlide || slideIndex <= (hasIntroSlide ? 1 : 0)
            ? undefined
            : () => {
                onMoveSlide({ slideIdx: slideIndex, direction: -1 });
              }
        }
        onMoveDown={
          !onMoveSlide ||
          isIntroSlide ||
          slideIndex === slideshowSlides.length - 1
            ? undefined
            : () => {
                onMoveSlide({ slideIdx: slideIndex, direction: 1 });
              }
        }
      />
    );
  };

  const classes = classNames({
    'slides-section-wrapper': true,
    'no-intro-slide': !hasIntroSlide,
    [className]: !!className,
  });

  return (
    <DndContext
      onDragEnd={onDragEnd}
      onDragStart={onDragStart}
      sensors={[mouseSensor, touchSensor]}
    >
      <SortableContext
        items={slideshowSlides.map(
          ({ photo, asset_image, account_card }) =>
            photo?.id ?? asset_image?.id ?? account_card?.id,
        )}
        strategy={verticalListSortingStrategy}
      >
        <div className={classes}>
          <div
            className={classNames({
              'w-100': true,
              'd-md-none': slidesTotal > 0,
            })}
          >
            {slidesTotal > 0 && (
              <p className="text-uppercase text-extra-bold text-light text-xs mb-0">
                Photo Slides
              </p>
            )}
            {slidesTotal > 0 && (
              <p className="mb-0 text-light text-italic text-xs ml-auto">
                Tap and drag slides to reorder
              </p>
            )}
          </div>
          <div
            className={classNames({
              'w-100': true,
              'h-100': !isMobile,
              'd-none': slidesTotal === 0,
            })}
          >
            <div className="slides-section-menu-container">
              <div className="w-100 d-none d-md-block mb-1">
                <p className="text-uppercase text-extra-bold text-light text-xs mb-0">
                  Photo Slides
                </p>
                {slidesTotal > 0 && (
                  <p className="mb-0 text-light text-italic text-xs ml-auto">
                    {isMobile ? 'Tap' : 'Click'} and drag slides to reorder
                  </p>
                )}
              </div>
              <AutoSizer defaultHeight={450}>
                {({ width, height }) => {
                  return (
                    <Grid
                      autoContainerWidth={!isMobile}
                      width={width}
                      height={height}
                      columnWidth={220}
                      rowHeight={isMobile ? 120 : 160}
                      columnCount={isMobile ? slideshowSlides.length : 1}
                      rowCount={isMobile ? 1 : slideshowSlides.length}
                      cellRenderer={cellRenderer}
                      className="slides-section-menu"
                      containerStyle={{ overflow: 'visible' }}
                      onSectionRendered={onSectionRendered}
                    />
                  );
                }}
              </AutoSizer>
              <DragOverlay>
                {draggingSlide && (
                  <div className="secondary-photos-view-slides-step-slide-thumbnail-overlay">
                    <SlideThumbnail
                      onClick={() => {}}
                      slideshowSlide={draggingSlide}
                    />
                  </div>
                )}
              </DragOverlay>
            </div>
          </div>
        </div>
      </SortableContext>
    </DndContext>
  );
};

SlidesSection.propTypes = propTypes;

export default SlidesSection;
